import React from "react";
import { TimestampView } from "../components/common/TimestampView";
import { AttachmentTargetType } from "../model/AttachmentTargetType";
import { Booking } from "./Booking.model";

interface AdminBookingDetailsProps {
  booking: Booking;
}

function renderObjectFields(
  obj: any,
  level = 0,
  seen = new WeakSet<any>()
): JSX.Element {
  const indent = { marginLeft: level * 12 } as React.CSSProperties;
  if (obj === null || obj === undefined) return <div style={indent}>-</div>;
  if (typeof obj !== "object") return <div style={indent}>{String(obj)}</div>;
  if (seen.has(obj)) return <div style={indent}>[Circular]</div>;
  seen.add(obj);
  if (Array.isArray(obj)) {
    return (
      <div>
        {obj.map((v, i) => (
          <div key={i} style={indent}>
            <strong>[{i}]</strong>
            {renderObjectFields(v, level + 1, seen)}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      {Object.keys(obj || {})
        .sort()
        .map((key) => (
          <div key={key} style={indent}>
            <strong>{key}:</strong>{" "}
            {typeof obj[key] === "object"
              ? renderObjectFields(obj[key], level + 1, seen)
              : String(obj[key])}
          </div>
        ))}
    </div>
  );
}

export function AdminBookingDetails({ booking }: AdminBookingDetailsProps) {
  return (
    <div className="card mt-4">
      <div className="card-body">
        <h5 className="mb-3">Admin: Booking details</h5>

        <div className="mb-3">
          <h6>Booking summary</h6>
          <div>
            <strong>ID:</strong> {booking.id || "-"}
          </div>
          <div>
            <strong>Title:</strong> {booking.bcd?.dl || "-"}
          </div>
          <div>
            <strong>Status:</strong>{" "}
            {booking.status || booking.statusDl?.dl || "-"}
          </div>
          <div>
            <strong>URL:</strong> {booking.url || "-"}
          </div>
        </div>

        <div className="mb-3">
          <h6>Common details (bcd)</h6>
          <div>
            <strong>Role:</strong> {booking.bcd?.role || "-"}
          </div>
          <div>
            <strong>Engine ID:</strong> {booking.bcd?.engineId || "-"}
          </div>
          <div>
            <strong>Bogie ID:</strong> {booking.bcd?.bogieId || "-"}
          </div>
          <div>
            <strong>Offering ID:</strong> {booking.bcd?.offeringId || "-"}
          </div>
          <div>
            <strong>Created at:</strong>{" "}
            {booking.bcd?.createdAt ? (
              <TimestampView
                timestamp={booking.bcd.createdAt}
                tzInToolTip={true}
              />
            ) : (
              "-"
            )}
          </div>
          <div>
            <strong>Cancelled at:</strong>{" "}
            {booking.bcd?.cancelledAt ? (
              <TimestampView
                timestamp={booking.bcd.cancelledAt}
                tzInToolTip={true}
              />
            ) : (
              "-"
            )}
          </div>
          <div>
            <strong>Engine completed at:</strong>{" "}
            {booking.bcd?.engineCompletedAt ? (
              <TimestampView
                timestamp={booking.bcd.engineCompletedAt}
                tzInToolTip={true}
              />
            ) : (
              "-"
            )}
          </div>
          <div>
            <strong>Bogie completed at:</strong>{" "}
            {booking.bcd?.bogieCompletedAt ? (
              <TimestampView
                timestamp={booking.bcd.bogieCompletedAt}
                tzInToolTip={true}
              />
            ) : (
              "-"
            )}
          </div>
          <div>
            <strong>Engine:</strong>{" "}
            {booking.bcd?.engine?.dl || booking.bcd?.engine?.id || "-"}
          </div>
          <div>
            <strong>Bogie:</strong>{" "}
            {booking.bcd?.bogie?.dl || booking.bcd?.bogie?.id || "-"}
          </div>
        </div>

        <div className="mb-3">
          <h6>Booking target</h6>
          <div>
            <strong>Type:</strong> {booking.bookingTarget?.targetType || "-"}
          </div>
          <div>
            <strong>Target ID:</strong> {booking.bookingTarget?.targetId || "-"}
          </div>
          <div>
            <strong>Target title (label):</strong>{" "}
            {booking.bookingTarget?.label || "-"}
          </div>
          <div>
            <strong>Target url:</strong> {booking.bookingTarget?.url || "-"}
          </div>
        </div>

        {booking.specificBookingTypeModel && (
          <div className="mb-3">
            <h6>Target specific details</h6>
            {booking.bookingTarget?.targetType ===
            AttachmentTargetType.VIDEO_SESSION ? (
              <div>
                <div>
                  <strong>Scheduled At:</strong>{" "}
                  {(booking.specificBookingTypeModel as any).scheduledAt ? (
                    <TimestampView
                      timestamp={
                        (booking.specificBookingTypeModel as any).scheduledAt
                      }
                      tzInToolTip={true}
                    />
                  ) : (
                    "-"
                  )}
                </div>
                <div>
                  <strong>Schedule date time:</strong>{" "}
                  {(booking.specificBookingTypeModel as any)
                    .scheduleDateTime ? (
                    <TimestampView
                      timestamp={
                        (booking.specificBookingTypeModel as any)
                          .scheduleDateTime
                      }
                      tzInToolTip={true}
                    />
                  ) : (
                    "-"
                  )}
                </div>
                <div>
                  <strong>Meeting link:</strong>{" "}
                  {(booking.specificBookingTypeModel as any)
                    .scheduleMeetingLink || "-"}
                </div>
                <div>
                  <strong>Display duration:</strong>{" "}
                  {(booking.specificBookingTypeModel as any).displayDuration ||
                    "-"}
                </div>
                <div>
                  <strong>Skill:</strong>{" "}
                  {(booking.specificBookingTypeModel as any).skill?.dl || "-"}
                </div>
                <div>
                  <strong>Agenda:</strong>{" "}
                  {(booking.specificBookingTypeModel as any).agenda || "-"}
                </div>
                <div>
                  <strong>Marked no-show at:</strong>{" "}
                  {(booking.specificBookingTypeModel as any).markedNoShowAt ? (
                    <TimestampView
                      timestamp={
                        (booking.specificBookingTypeModel as any).markedNoShowAt
                      }
                      tzInToolTip={true}
                    />
                  ) : (
                    "-"
                  )}
                </div>
                <div>
                  <strong>Can reschedule:</strong>{" "}
                  {String(
                    (booking.specificBookingTypeModel as any).canReschedule ||
                      false
                  )}
                </div>
                <div>
                  <strong>Can mark no-show:</strong>{" "}
                  {String(
                    (booking.specificBookingTypeModel as any).canMarkNoShow ||
                      false
                  )}
                </div>
                <div>
                  <strong>Expert reschedule notes:</strong>{" "}
                  {(booking.specificBookingTypeModel as any)
                    .expertRescheduleNotes || "-"}
                </div>
                <div>
                  <strong>Mentee reschedule notes:</strong>{" "}
                  {(booking.specificBookingTypeModel as any)
                    .menteeRescheduleNotes || "-"}
                </div>
              </div>
            ) : (
              <div>{renderObjectFields(booking.specificBookingTypeModel)}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminBookingDetails;
